/**
 * @fileoverview Service class for Active Substance API operations
 */

import axiosInstance from '../../services/api';

/**
 * Active Substance API Service
 * Handles all API operations for active substances
 */
export class ActiveSubstanceService {
  static baseUrl = '/api/active-substances';

  /**
   * Filter active substances with parameters
   * @param {Object} [params={}] - Filter parameters
   * @returns {Promise<Object>} Filtered substances with pagination
   */
  static async filterActiveSubstances(params = {}) {
    // Convert array parameters to comma-separated strings
    const processedParams = {};
    
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        processedParams[key] = value.join(',');
      } else if (value !== null && value !== undefined && value !== '') {
        processedParams[key] = value;
      }
    });


    const { data } = await axiosInstance.get(`${this.baseUrl}/filter/`, {
      params: processedParams,
    });

    return data;
  }

  /**
   * Retrieves an active substance by ID or name
   * @param {string|number} identifier - UUID or name of the active substance
   * @returns {Promise<Object>} The active substance data
   */
  static async getActiveSubstance(identifier) {
    const { data } = await axiosInstance.get(
      `${this.baseUrl}/${identifier}/?include_products=true&include_health=true&include_environment=true`
    );
    return data;
  }


  /**
   * Fetches culture tree for an active substance
   * @param {string} activeSubstanceId - Active substance ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Tree data response
   */
  static async getCultureTree(activeSubstanceId, params = {}) {
    try {
      const { data } = await axiosInstance.get(
        `/api/cultures/active-substances/${activeSubstanceId}/culture-tree/`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching culture tree:', error);
      throw error;
    }
  }
}

/**
 * Query keys factory for React Query cache management
 * Provides consistent keys for caching and invalidation
 */
export const activeSubstanceKeys = {
  all: ['activeSubstances'],
  filters: () => [...activeSubstanceKeys.all, 'filter'],
  filter: (params) => [...activeSubstanceKeys.filters(), params],
  details: () => [...activeSubstanceKeys.all, 'detail'],
  detail: (id) => [...activeSubstanceKeys.details(), id],
  cultureTree: (id, params) => [
    ...activeSubstanceKeys.detail(id),
    'cultureTree',
    params,
  ],
};
